import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { ArrowSquareOut } from "@phosphor-icons/react";
import TradingViewChart from "../components/TradingViewChart";

const EXCHANGES = ["BYBIT", "BINANCE", "COINBASE", "KRAKEN", "OKX", "BITSTAMP"];
const LIGHT_THEMES = ["arctic", "paper", "sakura", "mint", "citrus", "lavender"];

export default function Charts() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [params] = useSearchParams();
  const initialSymbol = params.get("symbol")?.toUpperCase().replace("/", "") || user?.settings?.default_symbol || "BTCUSDT";
  const [exchange, setExchange] = useState(null);
  const [symbol, setSymbol] = useState(initialSymbol);
  const [symbolInput, setSymbolInput] = useState(initialSymbol);

  useEffect(() => {
    api.get("/exchanges")
      .then(({ data }) => {
        const configured = data[0]?.exchange?.toUpperCase();
        setExchange(configured && EXCHANGES.includes(configured) ? configured : "BYBIT");
      })
      .catch(() => setExchange("BYBIT"));
  }, []);

  const tvSymbol = useMemo(() => `${exchange}:${symbol.toUpperCase()}`, [exchange, symbol]);
  const tvTheme = LIGHT_THEMES.includes(theme) ? "light" : "dark";

  if (!exchange) return <div className="text-muted-foreground p-8">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="charts-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <h1 className="font-heading font-bold text-3xl tracking-tight">Charts</h1>
        <div className="flex items-end gap-3">
          <div>
            <Label className="label-caps text-muted-foreground">Exchange</Label>
            <Select value={exchange} onValueChange={setExchange}>
              <SelectTrigger data-testid="chart-exchange-select" className="w-40 mt-1 font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXCHANGES.map((e) => (
                  <SelectItem key={e} value={e} className="font-mono">{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps text-muted-foreground">Symbol</Label>
            <form
              onSubmit={(e) => { e.preventDefault(); setSymbol(symbolInput.trim().toUpperCase()); }}
            >
              <Input
                data-testid="chart-symbol-input"
                className="w-44 mt-1 font-mono"
                value={symbolInput}
                onChange={(e) => setSymbolInput(e.target.value)}
                placeholder="BTCUSDT"
              />
            </form>
          </div>
          <Button data-testid="open-in-tradingview-btn" variant="outline"
            onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${tvSymbol}`, "_blank", "noopener")}>
            <ArrowSquareOut size={14} className="mr-1" /> Open in TradingView
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Showing <span className="font-mono text-foreground" data-testid="chart-active-symbol">{tvSymbol}</span> — defaults to your configured exchange. If the chart doesn't exist there, pick another exchange above.
        {" "}The embedded chart is anonymous; use "Open in TradingView" to view this symbol with your own TradingView account (drawings, layouts, indicators).
      </p>
      <div className="border border-border bg-card" style={{ height: "calc(100vh - 220px)", minHeight: 480 }}>
        <TradingViewChart symbol={tvSymbol} theme={tvTheme} />
      </div>
    </div>
  );
}
